/* eslint-disable indutrust-custom/prefer-semantic-theme-utilities -- fixed event-type status colors outside theme ramps */
import type {
  EventNotification,
  NotificationEventType,
} from '@indutrust/notification-model';

import {
  extractNotificationDescriptionParams,
  hasI18nNotificationDescription,
  resolveNotificationMessage,
  type NotificationDescriptionParams,
} from './notification-description.util';
import { resolveNotificationRoute } from './notification-route.util';

/** Presentation layout for notification list rows. */
export type NotificationLayout = 'page' | 'panel';

/** Pre-derived display fields for a notification list row. */
export type NotificationItemViewModel = {
  badgeClass: string;
  createdAt?: string;
  descriptionParams: NotificationDescriptionParams;
  hasI18nDescription: boolean;
  iconClass: string;
  iconName: string;
  id: number;
  isNavigable: boolean;
  message?: string;
  read: boolean;
  route: string[] | null;
  source: EventNotification;
  typeLabelKey: string;
};

/** Returns the PrimeIcons name for a notification event type. */
export function notificationIconName(
  eventType: NotificationEventType | undefined
): string {
  if (
    eventType === 'factory_published' ||
    eventType === 'capability_suggestion_approved'
  ) {
    return 'pi-check-circle';
  }
  if (
    eventType === 'factory_rejected' ||
    eventType === 'capability_suggestion_rejected'
  ) {
    return 'pi-times-circle';
  }
  if (eventType === 'profile_viewed') {
    return 'pi-eye';
  }
  if (eventType === 'project_factory_added') {
    return 'pi-folder-plus';
  }
  return 'pi-bell';
}

/** Returns the full PrimeIcons class string (with color) for page layout. */
export function notificationIconClass(
  eventType: NotificationEventType | undefined
): string {
  if (
    eventType === 'factory_published' ||
    eventType === 'capability_suggestion_approved'
  ) {
    return 'pi-check-circle text-green-500';
  }
  if (
    eventType === 'factory_rejected' ||
    eventType === 'capability_suggestion_rejected'
  ) {
    return 'pi-times-circle text-red-500';
  }
  if (eventType === 'capability_suggestion_submitted') {
    return 'pi-bell text-orange-500';
  }
  if (eventType === 'profile_viewed') {
    return 'pi-eye text-blue-500';
  }
  if (eventType === 'project_factory_added') {
    return 'pi-folder-plus text-green-500';
  }
  return 'pi-bell';
}

/** Returns Tailwind classes for the panel badge circle background. */
export function notificationBadgeClass(
  eventType: NotificationEventType | undefined
): string {
  if (
    eventType === 'factory_published' ||
    eventType === 'capability_suggestion_approved'
  ) {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
  if (
    eventType === 'factory_rejected' ||
    eventType === 'capability_suggestion_rejected'
  ) {
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  if (eventType === 'capability_suggestion_submitted') {
    return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
  }
  if (eventType === 'profile_viewed') {
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  }
  if (eventType === 'project_factory_added') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
  return 'bg-emphasis text-muted-color';
}

/**
 * Maps a notification entity to a template-ready view model.
 *
 * @param notification Source notification from the store.
 * @param _layout Reserved for layout-specific fields; both layouts share the same shape today.
 */
export function mapNotificationToViewModel(
  notification: EventNotification,
  _layout: NotificationLayout
): NotificationItemViewModel {
  const typeLabelKey = notification.event_type ?? 'default';
  const route = resolveNotificationRoute(notification);
  const descriptionParams = extractNotificationDescriptionParams(notification);

  return {
    badgeClass: notificationBadgeClass(notification.event_type),
    createdAt: notification.created_at,
    descriptionParams,
    hasI18nDescription: hasI18nNotificationDescription(notification.event_type),
    iconClass: notificationIconClass(notification.event_type),
    iconName: notificationIconName(notification.event_type),
    id: notification.id,
    isNavigable: route !== null,
    message: resolveNotificationMessage(notification),
    read: notification.read ?? false,
    route,
    source: notification,
    typeLabelKey,
  };
}
